import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  fetchCabinetSummaryThunk,
  JUST_ASKED_MS,
  selectCabinetSummary,
  selectCabinetSummaryFailed,
} from "./cabinet-summary.slice";

const REFRESH_EVERY_MS = 2 * 60 * 1000;

/**
 * Keeps the summary of the company current: asks now, again whenever the
 * window gets focus, and every two minutes while the tab is visible. The
 * dashboard calls it once for the whole cabinet. Opening and focus skip a request
 * that went out a few seconds ago (a page that just loaded, a click that gave the
 * window focus twice); the two-minute timer always asks.
 */
export function useCabinetSummaryRefresh(companyId: string | undefined, enabled: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!companyId || !enabled) return;
    const askUnlessJustAsked = () =>
      dispatch(fetchCabinetSummaryThunk(companyId, { maxAgeMs: JUST_ASKED_MS }));
    askUnlessJustAsked();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") dispatch(fetchCabinetSummaryThunk(companyId));
    }, REFRESH_EVERY_MS);
    window.addEventListener("focus", askUnlessJustAsked);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", askUnlessJustAsked);
    };
  }, [dispatch, companyId, enabled]);
}

/** What the summary holds for this company; `summary` is null until it has answered. */
export function useCabinetSummary(companyId: string | undefined) {
  const summary = useAppSelector((state) => selectCabinetSummary(state, companyId));
  const failed = useAppSelector((state) => selectCabinetSummaryFailed(state, companyId));
  return { summary, failed };
}
